/**
 * ParticipantsGrid -- editable, spreadsheet-like participant entry for
 * the Create Test wizard's Participants step. Replaces the Excel
 * upload/download flow with in-app row-by-row entry, same column
 * layout as the old template (Name, Email, LinkedIn URL, Timezone).
 *
 * The grid deliberately reads like a spreadsheet: the borders are on the
 * table cells (gridlines), not on the individual inputs -- each input
 * fills its cell edge-to-edge on a transparent background, and the active
 * cell shows an inset crimson focus ring. Column widths are fixed
 * (table-fixed + colgroup) so an inline validation message can never
 * resize a column.
 *
 * Presentational: timezone options and row data are owned by the
 * caller (ParticipantsStep) and passed in as props, matching how the
 * rest of this codebase's dynamic-row UIs (AssignParticipantsDialog)
 * keep data-fetching in the consuming page.
 */
import React from "react";
import { z } from "zod";
import { Button } from "../ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Combobox, type ComboboxOption } from "../Combobox";
import { Plus, Trash2 } from "lucide-react";

/** Rows shown by default before "Add row" is needed. */
export const DEFAULT_VISIBLE_ROWS = 5;

export interface ParticipantRow {
  id: string;
  name: string;
  email: string;
  linkedin_url: string;
  timezone: string;
}

function makeRow(): ParticipantRow {
  return {
    id: `row-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    email: "",
    linkedin_url: "",
    timezone: "",
  };
}

export function createDefaultRows(count: number = DEFAULT_VISIBLE_ROWS): ParticipantRow[] {
  return Array.from({ length: count }, () => makeRow());
}

// Mirrors backend/src/xyden/core/content/participant_validation.py exactly
// -- _EMAIL_RE and LINKEDIN_URL_RE -- so client-side feedback never
// disagrees with what POST /tests/participants/validate-rows will accept.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINKEDIN_URL_RE = /^https?:\/\/(?:[a-z0-9-]+\.)*linkedin\.com\/(?:in|pub)\/\S+/i;

const nameSchema = z.string().trim().min(1, "Name is required");
const emailSchema = z.string().trim().min(1, "Email is required").regex(EMAIL_RE, "Enter a valid email address");
const linkedinSchema = z
  .string()
  .trim()
  .min(1, "LinkedIn URL is required")
  .regex(LINKEDIN_URL_RE, "Enter a valid LinkedIn profile URL");
const timezoneSchema = z.string().trim().min(1, "Timezone is required");

export type ParticipantField = "name" | "email" | "linkedin_url" | "timezone";

/**
 * Format-only, client-side validation mirroring the backend's shared
 * validators. `validTimezones` is the set fetched from GET /api/timezones
 * -- pass an empty set to skip the membership check (e.g. before the
 * fetch resolves), since an empty set can never mean "nothing is valid."
 */
export function validateRow(
  row: ParticipantRow,
  validTimezones: Set<string>
): Partial<Record<ParticipantField, string>> {
  const errors: Partial<Record<ParticipantField, string>> = {};

  const nameResult = nameSchema.safeParse(row.name);
  if (!nameResult.success) errors.name = nameResult.error.issues[0].message;

  const emailResult = emailSchema.safeParse(row.email);
  if (!emailResult.success) errors.email = emailResult.error.issues[0].message;

  const linkedinResult = linkedinSchema.safeParse(row.linkedin_url);
  if (!linkedinResult.success) errors.linkedin_url = linkedinResult.error.issues[0].message;

  const timezoneResult = timezoneSchema.safeParse(row.timezone);
  if (!timezoneResult.success) {
    errors.timezone = timezoneResult.error.issues[0].message;
  } else if (validTimezones.size > 0 && !validTimezones.has(row.timezone.trim())) {
    errors.timezone = "Select a valid timezone from the list";
  }

  return errors;
}

// Spreadsheet cell: the input has no border of its own and fills the whole
// cell -- the gridlines come from the table cells. The active cell shows an
// inset crimson focus ring.
const CELL_INPUT_CLASS =
  "w-full h-10 bg-transparent px-3 text-sm text-gray-900 placeholder:text-brand-steel/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-crimson/40";

/** Body-cell class: right gridline + a subtle red tint when the cell errors. */
function cellClass(hasError: boolean): string {
  return `p-0 align-top border-r border-brand-steel/30${hasError ? " bg-red-50" : ""}`;
}

const HEAD_CLASS =
  "text-xs font-semibold text-gray-700 uppercase tracking-wider px-3 py-2.5 border-r border-brand-steel/30";

/**
 * ControlledInput wraps an input to maintain its own value state
 * while calling onChange immediately. This prevents the input from
 * resetting if the parent doesn't immediately update the rows prop.
 */
interface ControlledInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  ariaLabel: string;
  error?: string;
}

function ControlledInput({ value, onChange, onBlur, ariaLabel, error }: ControlledInputProps) {
  const [localValue, setLocalValue] = React.useState(value);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <>
      <input
        aria-label={ariaLabel}
        value={localValue}
        onChange={handleChange}
        onBlur={onBlur}
        className={CELL_INPUT_CLASS}
      />
      {error && <p className="text-xs text-brand-error px-3 pb-1.5">{error}</p>}
    </>
  );
}

export interface ParticipantsGridProps {
  rows: ParticipantRow[];
  onChange: (rows: ParticipantRow[]) => void;
  isBaseline?: boolean;
  timezoneOptions: ComboboxOption[];
  /** Keyed by `${rowId}.${fieldName}` -- see validateRow in this module. */
  fieldErrors?: Record<string, string>;
  onFieldBlur?: (rowId: string, field: keyof Omit<ParticipantRow, "id">) => void;
}

export function ParticipantsGrid({
  rows,
  onChange,
  isBaseline = false,
  timezoneOptions,
  fieldErrors = {},
  onFieldBlur,
}: ParticipantsGridProps) {
  const atCap = isBaseline && rows.length >= 1;

  const updateRow = (id: string, patch: Partial<ParticipantRow>) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id: string) => {
    onChange(rows.length === 1 ? rows : rows.filter((r) => r.id !== id));
  };
  const addRow = () => onChange([...rows, makeRow()]);

  return (
    <div>
      <div className="bg-white border border-brand-steel/50 rounded-lg shadow-sm mb-4 overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="table-fixed min-w-[760px]">
            <colgroup>
              <col className="w-12" />
              <col className="w-[19%]" />
              <col className="w-[25%]" />
              <col className="w-[29%]" />
              <col className="w-[19%]" />
              <col className="w-14" />
            </colgroup>
            <TableHeader className="bg-brand-panel">
              <TableRow className="border-b border-brand-steel/50 hover:bg-transparent">
                <TableHead className={`${HEAD_CLASS} text-center`}>#</TableHead>
                <TableHead className={HEAD_CLASS}>Name</TableHead>
                <TableHead className={HEAD_CLASS}>Email</TableHead>
                <TableHead className={HEAD_CLASS}>LinkedIn URL</TableHead>
                <TableHead className={HEAD_CLASS}>Timezone</TableHead>
                <TableHead className="px-2 py-2.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const nameError = fieldErrors[`${row.id}.name`];
                const emailError = fieldErrors[`${row.id}.email`];
                const linkedinError = fieldErrors[`${row.id}.linkedin_url`];
                const timezoneError = fieldErrors[`${row.id}.timezone`];
                return (
                  <TableRow key={row.id} className="border-b border-brand-steel/30 hover:bg-transparent">
                    <TableCell className="px-3 py-2 text-sm text-brand-steel text-center align-top border-r border-brand-steel/30">
                      {index + 1}
                    </TableCell>
                    <TableCell className={cellClass(!!nameError)}>
                      <ControlledInput
                        value={row.name}
                        onChange={(val) => updateRow(row.id, { name: val })}
                        onBlur={() => onFieldBlur?.(row.id, "name")}
                        ariaLabel={`Name for row ${index + 1}`}
                        error={nameError}
                      />
                    </TableCell>
                    <TableCell className={cellClass(!!emailError)}>
                      <ControlledInput
                        value={row.email}
                        onChange={(val) => updateRow(row.id, { email: val })}
                        onBlur={() => onFieldBlur?.(row.id, "email")}
                        ariaLabel={`Email for row ${index + 1}`}
                        error={emailError}
                      />
                    </TableCell>
                    <TableCell className={cellClass(!!linkedinError)}>
                      <ControlledInput
                        value={row.linkedin_url}
                        onChange={(val) => updateRow(row.id, { linkedin_url: val })}
                        onBlur={() => onFieldBlur?.(row.id, "linkedin_url")}
                        ariaLabel={`LinkedIn URL for row ${index + 1}`}
                        error={linkedinError}
                      />
                    </TableCell>
                    <TableCell className={cellClass(!!timezoneError)}>
                      <Combobox
                        options={timezoneOptions}
                        value={row.timezone}
                        onChange={(v) => {
                          updateRow(row.id, { timezone: v });
                          onFieldBlur?.(row.id, "timezone");
                        }}
                        placeholder="Select timezone"
                        searchPlaceholder="Search timezones"
                        maxRenderedOptions={50}
                        className="w-full h-10 border-0 rounded-none bg-transparent hover:bg-transparent px-3 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-crimson/40"
                      />
                      {timezoneError && <p className="text-xs text-brand-error px-3 pb-1.5">{timezoneError}</p>}
                    </TableCell>
                    <TableCell className="px-2 py-1.5 align-top text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-brand-steel hover:text-brand-error"
                        aria-label={`Remove row ${index + 1}`}
                        disabled={rows.length === 1}
                        onClick={() => removeRow(row.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {!atCap && (
          <Button
            type="button"
            variant="ghost"
            onClick={addRow}
            className="w-full h-auto justify-start gap-2 rounded-none cursor-pointer border-0 border-t border-brand-steel/50 px-4 py-2.5 text-sm font-medium text-brand-crimson hover:bg-brand-panel/50 hover:text-brand-crimson"
          >
            <Plus size={16} aria-hidden="true" /> Add row
          </Button>
        )}
      </div>
    </div>
  );
}

export default ParticipantsGrid;
